import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FieldLabel } from '@puckeditor/core'
import { Icon, LUCIDE_ICON_NAMES, parseIcon } from './Icon'
import { FA_ICON_NAMES } from './faIcons'
import * as styles from './IconField.module.scss'

export { Icon, parseIcon } from './Icon'
export type { IconProps, IconFamily } from './Icon'

type IconFieldProps = {
  value?: string
  onChange: (value: string) => void
  id?: string
  field?: { label?: string }
}

const FAMILIES: { key: 'lucide' | 'fa'; label: string; names: string[] }[] = [
  { key: 'lucide', label: 'Lucide', names: LUCIDE_ICON_NAMES },
  { key: 'fa', label: 'Font Awesome', names: FA_ICON_NAMES },
  // To add MUI: install @mui/icons-material, push { key: 'mui', ... } here and
  // handle the 'mui' family in Icon.tsx.
]

// Custom grid virtualization — only the visible rows are painted, so the full
// icon set scrolls smoothly without freezing the browser (no extra dep).
const CELL_MIN = 76
const ROW_HEIGHT = 64
const GRID_HEIGHT = 300
const OVERSCAN_ROWS = 2

function VirtualIconGrid({
  family,
  names,
  onSelect,
}: {
  family: 'lucide' | 'fa'
  names: string[]
  onSelect: (value: string) => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [scrollTop, setScrollTop] = useState(0)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const update = () => setWidth(el.clientWidth)
    update()
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // reset scroll when the result set changes (e.g. new search/tab)
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
    setScrollTop(0)
  }, [names])

  const columns = Math.max(1, Math.floor((width || CELL_MIN) / CELL_MIN))
  const cellWidth = width ? width / columns : CELL_MIN
  const totalRows = Math.ceil(names.length / columns)
  const totalHeight = totalRows * ROW_HEIGHT

  const startRow = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS)
  const endRow = Math.min(
    totalRows,
    Math.ceil((scrollTop + GRID_HEIGHT) / ROW_HEIGHT) + OVERSCAN_ROWS,
  )

  const cells: React.ReactNode[] = []
  for (let i = startRow * columns; i < Math.min(names.length, endRow * columns); i++) {
    const name = names[i]
    const value = `${family}:${name}`
    const row = Math.floor(i / columns)
    const col = i % columns
    cells.push(
      <button
        key={name}
        type="button"
        className={styles.cell}
        title={name}
        style={{
          position: 'absolute',
          top: row * ROW_HEIGHT,
          left: col * cellWidth,
          width: cellWidth,
          height: ROW_HEIGHT,
        }}
        onClick={() => onSelect(value)}
      >
        <Icon value={value} size={20} />
        <span className={styles.cellName}>{name}</span>
      </button>,
    )
  }

  return (
    <div
      className={styles.gridScroll}
      ref={scrollRef}
      style={{ height: GRID_HEIGHT }}
      onScroll={e => setScrollTop((e.target as HTMLDivElement).scrollTop)}
    >
      {names.length === 0 ? (
        <p className={styles.empty}>No icons match your search.</p>
      ) : (
        <div style={{ position: 'relative', height: totalHeight }}>{cells}</div>
      )}
    </div>
  )
}

function IconPickerPanel({
  value,
  containerRef,
  onSelect,
  onClose,
}: {
  value?: string
  containerRef: React.RefObject<HTMLDivElement>
  onSelect: (value: string) => void
  onClose: () => void
}) {
  // open on the tab matching the currently-selected icon's family
  const initialFamily = parseIcon(value)?.family
  const [tab, setTab] = useState<'lucide' | 'fa'>(
    initialFamily === 'fa' ? 'fa' : 'lucide',
  )
  const [query, setQuery] = useState('')

  const active = FAMILIES.find(f => f.key === tab) ?? FAMILIES[0]

  const inputRef = useRef<HTMLInputElement>(null)

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    const names = q
      ? active.names.filter(n => n.toLowerCase().includes(q))
      : active.names
    return names
  }, [active, query])

  // Focus search without scrolling the page to the input (the field can sit
  // off-screen in the puck side panel — plain autoFocus jumps the scroll).
  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true })
  }, [])

  // Close on outside-click (anywhere outside the field wrapper) or Escape.
  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onClose()
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDocDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [containerRef, onClose])

  return (
    <div className={styles.panel} role="dialog">
      <div className={styles.tabs}>
        {FAMILIES.map(f => (
          <button
            key={f.key}
            type="button"
            className={`${styles.tab} ${f.key === tab ? styles.tabActive : ''}`}
            onClick={() => setTab(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>
      <input
        ref={inputRef}
        className={styles.search}
        type="text"
        placeholder={`Search ${active.label} icons…`}
        value={query}
        onChange={e => setQuery(e.target.value)}
      />
      <VirtualIconGrid family={tab} names={matches} onSelect={onSelect} />
      <p className={styles.note}>{matches.length} icons</p>
    </div>
  )
}

export const IconField = ({ value, onChange, field }: IconFieldProps) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const parsed = parseIcon(value)

  return (
    <FieldLabel label={field?.label ?? 'Icon'}>
      <div className={styles.wrap} ref={wrapRef}>
        <button
          type="button"
          className={styles.trigger}
          onClick={() => setOpen(o => !o)}
        >
          {parsed ? (
            <>
              <Icon value={value} size={18} />
              <span className={styles.triggerName}>{parsed.name}</span>
            </>
          ) : (
            <span className={styles.placeholder}>Select an icon…</span>
          )}
        </button>
        {value && (
          <button
            type="button"
            className={styles.clear}
            aria-label="Clear icon"
            onClick={() => onChange('')}
          >
            ×
          </button>
        )}
        {open && (
          <IconPickerPanel
            value={value}
            containerRef={wrapRef}
            onSelect={v => {
              onChange(v)
              setOpen(false)
            }}
            onClose={() => setOpen(false)}
          />
        )}
      </div>
    </FieldLabel>
  )
}

/** Reusable custom-field definition. Spread `{ ...iconField, label: '…' }`. */
export const iconField = {
  type: 'custom' as const,
  label: 'Icon',
  render: (props: IconFieldProps) => <IconField {...props} />,
}
